import { Component } from "react";
import {
  getPopularMoods,
  getSuggestedMoods,
  SHUFFLE_MOODS,
} from "../shuffle-moods";
import { getSlowedMemory, recordMoodUsed } from "../slowed-memory";

const SUGGESTED_COUNT = 3;

function SparkleIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6L4.5 10l5.6-1.9L12 2.5z"
        fill="currentColor"
      />
      <path
        d="M18.5 14.5l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6z"
        fill="currentColor"
        opacity="0.7"
      />
      <path
        d="M5 15l.6 1.7 1.7.6-1.7.6L5 19.6l-.6-1.7-1.7-.6 1.7-.6L5 15z"
        fill="currentColor"
        opacity="0.55"
      />
    </svg>
  );
}

export default class SlowedAI extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      generating: false,
      lastAppliedName: null,
      memory: { recents: [], likes: [], customMoods: [] },
      memoryLoaded: false,
    };
    this.rootRef = null;
    this.closeTimer = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onRootMouseEnter = this.onRootMouseEnter.bind(this);
    this.onRootMouseLeave = this.onRootMouseLeave.bind(this);
  }

  async componentDidMount() {
    document.addEventListener("mousedown", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeyDown);
    await this.refreshMemory();
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeyDown);
    clearTimeout(this.appliedResetTimer);
    clearTimeout(this.closeTimer);
  }

  onRootMouseEnter() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    if (this.props.isProFeaturesLocked) return;
    this.openPanel("hover");
  }

  onRootMouseLeave() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (this.state.open) this.setState({ open: false });
    }, 220);
  }

  async refreshMemory() {
    try {
      const memory = await getSlowedMemory();
      this.setState({ memory, memoryLoaded: true });
    } catch (_e) {
      this.setState({ memoryLoaded: true });
    }
  }

  onDocumentClick(event) {
    if (!this.state.open) return;
    if (this.rootRef && this.rootRef.contains(event.target)) return;
    this.setState({ open: false });
  }

  onKeyDown(event) {
    if (event.key === "Escape" && this.state.open) {
      this.setState({ open: false });
    }
  }

  togglePanel() {
    if (this.props.isProFeaturesLocked) {
      this.props.onLockedClick?.({ source: "fab" });
      this.props.onUpgradeClick?.();
      return;
    }
    // Open on click — closing is handled by mouseleave, Escape, or
    // clicking outside the panel. Otherwise a click while the panel is
    // already open (e.g. opened by hover) would immediately close it.
    this.openPanel("click");
  }

  openPanel(source) {
    if (this.state.open) return;
    this.setState({ open: true });
    this.props.onOpen?.({ source });
  }

  findMood(name) {
    return SHUFFLE_MOODS.find((v) => v.name === name) || null;
  }

  async runGenerate() {
    if (this.state.generating) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onLockedClick?.({ source: "generate" });
      this.props.onUpgradeClick?.();
      return;
    }
    this.setState({ generating: true, lastAppliedName: null });
    try {
      const mood = await this.props.onGenerateMood?.();
      if (mood?.name) {
        const memory = await recordMoodUsed(mood.name);
        if (memory) this.setState({ memory });
      }
      this.flashApplied(mood?.name || "✨ Mood");
    } finally {
      this.setState({ generating: false });
    }
  }

  async pickMood(mood) {
    if (this.props.isProFeaturesLocked) {
      this.props.onLockedClick?.({ source: "mood", mood: mood?.name });
      this.props.onUpgradeClick?.();
      return;
    }
    if (this.state.generating) return;
    this.setState({ generating: true, lastAppliedName: null });
    try {
      await this.props.onApplyMood?.(mood);
      const memory = await recordMoodUsed(mood.name);
      if (memory) this.setState({ memory });
      this.flashApplied(mood.name);
    } finally {
      this.setState({ generating: false });
    }
  }

  flashApplied(name) {
    this.setState({ lastAppliedName: name });
    clearTimeout(this.appliedResetTimer);
    this.appliedResetTimer = setTimeout(() => {
      this.setState({ lastAppliedName: null });
    }, 2200);
  }

  renderQuickRow(label, moods) {
    if (!moods.length) return null;
    return (
      <div className="slowed-ai__quick">
        <div className="slowed-ai__quick-label">{label}</div>
        <div className="slowed-ai__chips">
          {moods.map((mood) => (
            <button
              key={`${label}-${mood.name}`}
              type="button"
              className="slowed-ai__chip"
              onClick={() => this.pickMood(mood)}
              disabled={this.state.generating}
              title={mood.name}
            >
              {mood.emoji && (
                <span aria-hidden="true">{mood.emoji}</span>
              )}
              <span className="slowed-ai__chip-name">{mood.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const { open, generating, lastAppliedName, memory } = this.state;
    const { isProFeaturesLocked, trackName } = this.props;

    const likedSet = new Set(memory.likes || []);
    const likedMoods = SHUFFLE_MOODS.filter((m) => likedSet.has(m.name)).slice(
      0,
      4,
    );
    const suggested = getSuggestedMoods(
      SUGGESTED_COUNT,
      new Date(),
      trackName,
    );

    return (
      <div
        className={`slowed-ai ${open ? "slowed-ai--open" : ""} ${generating ? "slowed-ai--busy" : ""}`}
        ref={(el) => (this.rootRef = el)}
        onMouseEnter={this.onRootMouseEnter}
        onMouseLeave={this.onRootMouseLeave}
      >
        {open && (
          <div className="slowed-ai__panel" role="dialog" aria-label="Slowed AI">
            <div className="slowed-ai__panel-header">
              <div className="slowed-ai__brand">
                <SparkleIcon className="slowed-ai__brand-icon" />
                <span className="slowed-ai__brand-name">Slowed</span>
                <span className="slowed-ai__brand-tag">AI</span>
                <span className="slowed-ai__brand-beta">beta</span>
              </div>
              <p className="slowed-ai__panel-subtitle">
                Pick a slowed mood or generate a fresh one.
              </p>
            </div>

            {this.renderQuickRow("Saved", likedMoods)}
            {this.renderQuickRow("Popular", getPopularMoods())}
            {this.renderQuickRow("Suggested", suggested)}

            <div className="slowed-ai__footer">
              <button
                type="button"
                className="slowed-ai__generate"
                onClick={() => this.runGenerate()}
                disabled={generating}
                aria-live="polite"
              >
                <SparkleIcon className="slowed-ai__generate-icon" />
                <span
                  key={generating ? "vibing" : lastAppliedName || "idle"}
                  className="slowed-ai__generate-label"
                >
                  {generating
                    ? "Slowing..."
                    : lastAppliedName
                      ? lastAppliedName
                      : "Generate"}
                </span>
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          className="slowed-ai__fab"
          onClick={() => this.togglePanel()}
          aria-expanded={open}
          aria-label={open ? "Close Slowed AI" : "Open Slowed AI"}
          aria-busy={generating}
          disabled={isProFeaturesLocked && false}
        >
          <span className="slowed-ai__fab-glow" aria-hidden="true" />
          <span className="slowed-ai__fab-ring" aria-hidden="true" />
          <SparkleIcon className="slowed-ai__fab-icon" />
        </button>
      </div>
    );
  }
}
